import * as React from "react"
import { CgNotes } from "react-icons/cg"
import { IoIosRibbon, IoMdAttach } from "react-icons/io"
import { gql } from "@apollo/client"
import {
  Avatar,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  HStack,
  Link,
  LinkBox,
  LinkOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import { nestedPaginate } from "lib/apollo/helpers"
import {
  ProfileActCompetitionFragment,
  ProfileActFragment,
  SortOrder,
  useGetProfileActsQuery,
  useUpdateActFeedbackSeenMutation,
} from "lib/graphql"
import { useMutationHandler } from "lib/hooks/useMutationHandler"

import { useMe } from "../lib/hooks/useMe"
import { LinkButton } from "./LinkButton"
import { Modal } from "./Modal"

dayjs.extend(utc)

const _ = gql`
  fragment ProfileAct on Act {
    id
    name
    category
    creatorId
    isMissingInfo
    isEditableAfterCompetitionClosed
    hasFeedback
    feedbackUpload
    preliminaryPosition
    finalPosition
    feedbackNotes
    status
    competition {
      ...ProfileActCompetition
    }
  }

  fragment ProfileActCompetition on Competition {
    id
    name
    slug
    year
    thumbnail
    isFinal
    ...CompetitionDeadlines
  }

  query GetProfileActs(
    $id: String!
    $take: Int
    $orderBy: [ActOrderByWithRelationInput!]
    $where: ActWhereInput
    $skip: Int
  ) {
    user(id: $id) {
      id
      acts(take: $take, orderBy: $orderBy, where: $where, skip: $skip) {
        items {
          ...ProfileAct
        }
        count
      }
    }
  }

  mutation UpdateActFeedbackSeen($id: String!, $data: UpdateActInput!) {
    updateActFeedback(id: $id, data: $data) {
      ...ProfileAct
    }
  }
`

interface Props {
  userId: string
}

const TAKE = 8

export function ProfileActs({ userId }: Props) {
  const { me } = useMe()
  const isCurrentUser = me?.id === userId

  const { data, loading, fetchMore } = useGetProfileActsQuery({
    variables: {
      id: userId,
      take: TAKE,
      orderBy: {
        createdAt: SortOrder.Desc,
      },
    },
  })

  const acts = data?.user?.acts.items
  const actsCount = data?.user?.acts.count

  const groupedActs = React.useMemo(
    () =>
      acts?.reduce((acc, act) => {
        const year = act.competition.year as number
        if (!acc[year]) {
          acc[year] = []
        }
        acc[year].push(act)
        return acc
      }, {} as { [year: number]: ProfileActFragment[] }),
    [acts],
  )

  const handleFetchMore = () => {
    if (!acts) return
    return fetchMore({
      variables: { skip: acts.length, take: TAKE },
      updateQuery: nestedPaginate("user", "acts"),
    })
  }
  if (loading)
    return (
      <Center>
        <Spinner />
      </Center>
    )
  return (
    <Box>
      {!!!acts || acts.length === 0 ? (
        isCurrentUser ? (
          <Stack mt={8} spacing={8} align="center">
            <Text fontWeight="bold" fontSize="xl" color="yellow.500">
              Jaaa dit is HET moment om jouw act aan te maken! Start hier.
            </Text>
          </Stack>
        ) : (
          <Center>
            <Text mt={8} color="gray.500">
              Geen wedstrijden
            </Text>
          </Center>
        )
      ) : (
        groupedActs &&
        Object.entries(groupedActs)
          .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
          .map(([year, acts]) => (
            <Stack key={year}>
              <Flex alignItems="center" mt={10} mb={2}>
                <Heading color="gray.400" fontSize="2xl">
                  {year}
                </Heading>
              </Flex>

              <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={5}>
                {acts.map((act: ProfileActFragment) => (
                  <ProfileActTile
                    key={act.id}
                    act={act}
                    isGroupMember={act.creatorId !== userId}
                    isMissingInfo={act.isMissingInfo}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          ))
      )}
      {actsCount && acts && acts.length < actsCount ? (
        <Box pt={6}>
          <Button type="button" variant="outline" onClick={handleFetchMore} size="sm" colorScheme="gray">
            Meer laden
          </Button>
        </Box>
      ) : null}
      {isCurrentUser && (
        <Stack align="center" pt={10}>
          <LinkButton href="/wedstrijd" size="md" variant="solid" colorScheme="yellow">
            Vind je wedstrijd
          </LinkButton>
        </Stack>
      )}
    </Box>
  )
}

interface ProfileActTileProps {
  act: ProfileActFragment
  isGroupMember: boolean
  isMissingInfo: boolean
}

const ProfileActTile: React.FC<ProfileActTileProps> = ({ act, isGroupMember, isMissingInfo }) => {
  const { me, refetch } = useMe()
  const modalProps = useDisclosure()
  const handler = useMutationHandler()
  const [updateAct] = useUpdateActFeedbackSeenMutation()

  const openFeedback = () => {
    modalProps.onOpen()
    handler(() => updateAct({ variables: { data: { feedbackSeen: true }, id: act.id } }), {
      onSuccess: () => {
        refetch()
      },
    })
  }

  const deadline = act.competition[
    `deadline${act.category.replace(/\s/g, "")}` as keyof ProfileActCompetitionFragment
  ] as Date | undefined

  const isPastDeadline = deadline ? dayjs.utc(deadline).add(1, "day").isBefore(dayjs().endOf("day")) : false

  const isPastFirstRound = act.competition?.firstRoundDate
    ? dayjs.utc(act.competition?.firstRoundDate).add(1, "day").isBefore(dayjs().endOf("day"))
    : false

  const isMyAct = act.creatorId === me?.id

  return (
    <>
      <Box position="relative">
        <LinkBox position="relative" overflow="hidden">
          <Stack
            width="100%"
            bg="gray.700"
            justify="space-between"
            bgImage={act.competition.thumbnail || undefined}
            bgSize="cover"
            bgRepeat="no-repeat"
            bgBlendMode="overlay"
            borderRadius="md"
            position="relative"
            py={6}
            px={4}
          >
            <LinkOverlay
              href={
                isMyAct
                  ? `/wedstrijd/${act.competition.slug}/apply/?actId=${act.id}`
                  : `/wedstrijd/${act.competition.slug}`
              }
              textDecoration="none"
            >
              <Flex
                flexDir="column"
                width="100%"
                position="absolute"
                top={0}
                left={0}
                borderTopRadius="md"
                overflow="hidden"
                zIndex={20}
              >
                {act.creatorId === me?.id && isMissingInfo && !isPastFirstRound && !isPastDeadline && (
                  <Box w="100%" bgColor="pink.500" px={0} py={0}>
                    <Text color="black" fontWeight="bold" fontSize="md" textAlign="center">
                      Kun je nog iets meer info invullen? Je bent er bijna!
                    </Text>
                  </Box>
                )}
                {isPastDeadline && !isPastFirstRound && act.creatorId === me?.id && (
                  <Box w="100%" bgColor="pink.500" px={0} py={0}>
                    <Text color="black" fontWeight="bold" fontSize="md" textAlign="center">
                      Deadline verstreken
                    </Text>
                  </Box>
                )}
                {isGroupMember && (
                  <Box w="100%" bgColor="yellow.500" px={0} py={0}>
                    <Text color="black" fontWeight="bold" fontSize="md" textAlign="center">
                      Group member
                    </Text>
                  </Box>
                )}
                {isPastFirstRound && act.creatorId === me?.id && (
                  <Box w="100%" bgColor="yellow.500" px={0} py={0}>
                    <Text color="black" fontWeight="bold" fontSize="md" textAlign="center">
                      Voorronde klaar!
                    </Text>
                  </Box>
                )}
                {(act.status === "REJECTED" || act.status === "REJECTED_PRE_ROUND") &&
                  act.creatorId === me?.id && (
                    <Box w="100%" bgColor="pink.500" px={0} py={0}>
                      <Text color="black" fontWeight="bold" fontSize="md" textAlign="center">
                        Niet meer beschikbaar
                      </Text>
                    </Box>
                  )}
              </Flex>
              <Stack zIndex={1} minH="270px">
                <Stack spacing={0}>
                  <Text
                    fontWeight="extrabold"
                    fontSize="4xl"
                    textTransform="uppercase"
                    lineHeight="short"
                    color="white"
                  >
                    {act.name}
                  </Text>
                  <Text fontWeight="extrabold" fontSize="xl" lineHeight="short" mb={2} color="gray.400">
                    {act.category}
                  </Text>
                </Stack>
                <Text
                  fontWeight="extrabold"
                  fontSize="2xl"
                  textTransform="uppercase"
                  lineHeight="short"
                  mb={2}
                  color="gray.200"
                >
                  {act.competition.name}
                </Text>
                <Text fontWeight="bold" fontSize={22} color="gray.400">
                  {act.competition.firstRoundDate
                    ? dayjs.utc(act.competition.firstRoundDate).format("DD/MM/YYYY")
                    : "Datum volgt"}
                </Text>
              </Stack>
              {deadline && !isPastFirstRound && (
                <Text fontWeight="bold" fontSize={16} color="yellow.500" zIndex={1}>
                  Deadline: {dayjs.utc(deadline).format("DD/MM/YYYY")}
                </Text>
              )}
            </LinkOverlay>
            {act.hasFeedback && act.creatorId === me?.id && (
              <Button
                leftIcon={<CgNotes />}
                w="fit-content"
                px={me?.hasPendingFeedback ? 4 : 0}
                variant={me?.hasPendingFeedback ? "solid" : "ghost"}
                colorScheme="yellow"
                _hover={{ bgColor: "yellow.600" }}
                onClick={openFeedback}
                cursor="pointer"
              >
                Jury scorekaart
              </Button>
            )}
          </Stack>
        </LinkBox>
        {((act.preliminaryPosition && !act.competition.isFinal) ||
          (act.finalPosition && act.competition.isFinal)) && (
          <Box position="absolute" bottom="0px" right="-25px" zIndex={2}>
            <Box as={IoIosRibbon} color="yellow.500" size="120px" zIndex={3} />
            <Text
              color="yellow.500"
              fontSize="lg"
              fontWeight="extrabold"
              position="absolute"
              top="21px"
              left="42px"
              bgColor="gray.700"
              px="13px"
              py="4px"
              borderRadius="50%"
              zIndex={1}
            >
              {act.finalPosition || act.preliminaryPosition}
            </Text>
          </Box>
        )}

        {act.hasFeedback && act.creatorId === me?.id && (
          <Flex justify="center" mt={4}>
            <Modal {...modalProps} size="2xl">
              <HStack align="flex-start" py={8}>
                <Box p={2}>
                  <Avatar src="/avatar.png" size="md" borderColor="black" name="avatar" />
                </Box>
                <Stack>
                  {act.feedbackNotes && (
                    <Text fontSize="lg" mb={2} p={4} borderRadius="xl" bg="gray.800">
                      {act.feedbackNotes}
                    </Text>
                  )}
                  {act.feedbackUpload && (
                    <Link
                      target="_blank"
                      w="100%"
                      href={act.feedbackUpload}
                      isExternal
                      aria-label="Jury scorekaart (wordt geopend in een nieuw tabblad)"
                    >
                      <HStack align="center" justify="flex-start">
                        <IoMdAttach size="20px" color="gray.400" />
                        <Text borderRadius="2xl" px={4} py={2} bg="gray.800" w="fit-content">
                          Jury scorekaart
                        </Text>
                      </HStack>
                    </Link>
                  )}
                </Stack>
              </HStack>
            </Modal>
          </Flex>
        )}
      </Box>
    </>
  )
}
